import {
  Trash2, Warehouse, Truck, Plus, ArrowRight, Loader2,
} from 'lucide-react';
import type {
  CallOff, CallOffLine, AvailablePartForCallOff, CallOffDeliveryDestination,
} from '../../../lib/api';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';

interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

interface CallOffLinesTableProps {
  callOff: CallOff;
  inlineEditMode: boolean;
  inlineEditChanges: Record<number, InlineEditChange>;
  editingLineId: number | null;
  editingField: string | null;
  editValue: string;
  savingEdit: boolean;
  supplierCanEditLines: boolean;
  onQuantityChange: (line: CallOffLine, newQty: string) => void;
  onStartLineEdit: (lineId: number, field: string, currentValue: string) => void;
  onSaveLineEdit: () => void;
  onCancelLineEdit: () => void;
  setEditValue: (v: string) => void;
  removeLineItem: (line: CallOffLine) => void;
  /** Add parts state */
  showAddParts: boolean;
  availableParts: AvailablePartForCallOff[];
  availableDeliveries: CallOffDeliveryDestination[];
  selectedDelivery: string;
  setSelectedDelivery: (v: string) => void;
  addPartsLoading: boolean;
  partQuantities: Record<number, number>;
  setPartQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  addingPart: boolean;
  handleAddPart: (part: AvailablePartForCallOff) => void;
  isSupplier: boolean;
  isLogistics?: boolean;
  /** Per-part PO availability budget. When a warehouse line's part is over,
   *  the qty input goes red and the parent disables Save Changes. */
  overByPart?: Record<string, { effective: number; consumed: number; over: boolean }>;
}

/**
 * Lines table for the call-off detail modal.
 * Groups lines by delivery type (warehouse / direct) with section headers.
 */
export default function CallOffLinesTable({
  callOff, inlineEditMode, inlineEditChanges,
  editingLineId: _editingLineId, editingField: _editingField,
  editValue: _editValue, savingEdit: _savingEdit,
  supplierCanEditLines: _supplierCanEditLines,
  onQuantityChange,
  onStartLineEdit: _onStartLineEdit, onSaveLineEdit: _onSaveLineEdit,
  onCancelLineEdit: _onCancelLineEdit, setEditValue: _setEditValue,
  removeLineItem,
  showAddParts, availableParts, availableDeliveries,
  selectedDelivery, setSelectedDelivery,
  addPartsLoading, partQuantities, setPartQuantities, addingPart, handleAddPart,
  isSupplier: _isSupplier,
  isLogistics = false,
  overByPart,
}: CallOffLinesTableProps) {
  const warehouseLines = callOff.lines.filter((l: CallOffLine) => l.delivery_type === 'warehouse');
  const directLines = callOff.lines.filter((l: CallOffLine) => l.delivery_type !== 'warehouse');
  const colSpan = 7 + (isLogistics ? 1 : 0) + (inlineEditMode ? 1 : 0);

  // Pallet totals for logistics summary. Uses the in-progress edit value when
  // a line is being batch-edited so the totals stay live.
  const palletsForLine = (line: CallOffLine): number => {
    const change = inlineEditChanges[line.id];
    const qty = parseFloat(change ? change.newQty : line.requested_qty) || 0;
    const palletQty = parseFloat(line.pallet_qty) || 0;
    return palletQty > 0 ? qty / palletQty : 0;
  };

  const warehousePalletTotals = warehouseLines.reduce<Record<string, number>>((acc, line) => {
    const dest = line.delivery_destination_name || 'Warehouse';
    acc[dest] = (acc[dest] || 0) + palletsForLine(line);
    return acc;
  }, {});
  const directPalletTotal = directLines.reduce((sum, line) => sum + palletsForLine(line), 0);
  const grandPalletTotal =
    Object.values(warehousePalletTotals).reduce((a, b) => a + b, 0) + directPalletTotal;

  const renderLine = (line: CallOffLine) => {
    const batchChange = inlineEditChanges[line.id];
    const displayQty = batchChange ? batchChange.newQty : line.requested_qty;
    const loadQty = parseFloat(line.load_qty) || 0;
    const lineQty = parseFloat(displayQty) || 0;
    const lineFill = loadQty > 0 ? Math.round((lineQty / loadQty) * 100) : 0;
    const palletQty = parseFloat(line.pallet_qty) || 0;
    const pallets = palletQty > 0 ? (lineQty / palletQty).toFixed(2) : '—';
    const partInfo = line.delivery_type === 'warehouse' ? overByPart?.[line.part_number] : undefined;
    const isOver = !!partInfo?.over;

    return (
      <TableRow key={line.id} className={isOver ? 'bg-red-50' : (batchChange ? 'bg-amber-50' : '')}>
        <TableCell className="font-mono text-xs font-medium">{line.part_number}</TableCell>
        <TableCell className="text-sm">{line.description}</TableCell>
        <TableCell>
          {line.delivery_type === 'warehouse'
            ? line.delivery_destination_name || 'Warehouse'
            : line.delivery_address || line.sales_order || 'Direct'}
        </TableCell>
        <TableCell className="text-right">
          {inlineEditMode ? (
            <div className="flex flex-col items-end gap-0.5">
              <Input
                type="number"
                className={`h-7 w-20 text-right text-sm ${
                  isOver ? 'border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500' : ''
                }`}
                value={displayQty}
                onChange={(e) => onQuantityChange(line, e.target.value)}
                min={0}
              />
              {isOver && partInfo && (
                <span className="text-[10px] text-red-700 whitespace-nowrap">
                  {partInfo.effective === 0
                    ? 'No PO available'
                    : `Only ${partInfo.effective} available`}
                </span>
              )}
            </div>
          ) : (
            <span>{line.requested_qty}</span>
          )}
        </TableCell>
        <TableCell className="text-right">{line.load_qty}</TableCell>
        {isLogistics && (
          <TableCell className="text-right font-medium">{pallets}</TableCell>
        )}
        <TableCell className="text-right">
          <Badge
            variant="secondary"
            className={lineFill >= 100 ? 'bg-green-100 text-green-700' : lineFill >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}
          >
            {lineFill}%
          </Badge>
        </TableCell>
        <TableCell>
          <span className="text-xs text-muted-foreground">{line.notes || ''}</span>
        </TableCell>
        {inlineEditMode && (
          <TableCell className="text-center">
            <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700" onClick={() => removeLineItem(line)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <>
      {callOff.lines && callOff.lines.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Part Number</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead className="text-right">Requested</TableHead>
                            <TableHead className="text-right">Load Qty</TableHead>
              {isLogistics && <TableHead className="text-right">Pallets</TableHead>}
              <TableHead className="text-right">Fill %</TableHead>
              <TableHead>Notes</TableHead>
              {inlineEditMode && <TableHead className="text-center">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouseLines.length > 0 && (
              <>
                <TableRow className="bg-red-50 hover:bg-red-50">
                  <TableCell colSpan={colSpan} className="py-2">
                    <div className="flex items-center gap-1.5 font-semibold text-red-700 text-xs uppercase tracking-wide">
                      <Warehouse className="h-3.5 w-3.5" />
                      Warehouse Delivery ({warehouseLines.length} items)
                    </div>
                  </TableCell>
                </TableRow>
                {warehouseLines.map(renderLine)}
              </>
            )}
            {directLines.length > 0 && (
              <>
                <TableRow className="bg-blue-50 hover:bg-blue-50">
                  <TableCell colSpan={colSpan} className="py-2">
                    <div className="flex items-center gap-1.5 font-semibold text-blue-700 text-xs uppercase tracking-wide">
                      <Truck className="h-3.5 w-3.5" />
                      Direct Delivery ({directLines.length} items)
                    </div>
                  </TableCell>
                </TableRow>
                {directLines.map(renderLine)}
              </>
            )}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-muted-foreground">No lines.</div>
      )}

      {/* Logistics: pallet totals summary */}
      {isLogistics && callOff.lines && callOff.lines.length > 0 && (
        <Card className="mt-4 border-blue-200">
          <CardHeader className="py-2 px-4 bg-blue-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Pallet Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-1.5 text-sm">
              {Object.entries(warehousePalletTotals).map(([dest, total]) => (
                <div key={dest} className="flex justify-between">
                  <span className="text-muted-foreground">{dest}</span>
                  <span className="font-medium">{total.toFixed(2)} pallets</span>
                </div>
              ))}
              {directPalletTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Direct Deliveries</span>
                  <span className="font-medium">{directPalletTotal.toFixed(2)} pallets</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1.5 mt-1.5 font-semibold">
                <span>Total</span>
                <span>{grandPalletTotal.toFixed(2)} pallets</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Add Parts Section */}
      {inlineEditMode && showAddParts && (
        <Card className="mt-4 border-blue-200">
          <CardHeader className="py-3 px-4 bg-blue-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Parts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 p-2 rounded bg-blue-50 text-blue-700 text-xs">
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              Select a warehouse destination, then add products with quantities.
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Warehouse Destination</Label>
              <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select warehouse..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDeliveries
                    .map((d, idx) => ({ d, idx }))
                    .filter(({ d }) => d.delivery_type === 'warehouse')
                    .map(({ d, idx }) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {d.delivery_destination_name || 'Warehouse'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {addPartsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : availableParts.length > 0 ? (
              <div className="max-h-[250px] overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Part No</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-right">Load Qty</TableHead>
                      <TableHead className="text-xs text-right w-[80px]">Qty</TableHead>
                      <TableHead className="text-xs text-center w-[70px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableParts.map(part => (
                      <TableRow key={part.id}>
                        <TableCell className="font-mono text-xs">{part.part_number}</TableCell>
                        <TableCell className="text-xs">{part.description}</TableCell>
                        <TableCell className="text-xs text-right">{part.load_qty}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="h-6 w-[70px] text-right text-xs ml-auto"
                            min={0}
                            value={partQuantities[part.id] || ''}
                            onChange={(e) => setPartQuantities(prev => ({
                              ...prev,
                              [part.id]: parseFloat(e.target.value) || 0,
                            }))}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2"
                            disabled={addingPart || !selectedDelivery || !(partQuantities[part.id] > 0)}
                            onClick={() => handleAddPart(part)}
                          >
                            Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-3">
                No additional parts available to add.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {callOff.notes && (
        <Card className="mt-4">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4 text-sm">{callOff.notes}</CardContent>
        </Card>
      )}
    </>
  );
}
